import { useSyncExternalStore } from 'react';

/**
 * Soft goods lot selection for the donation currently open.
 *
 * Deliberately NOT kept in react-final-form state. The loader has to seed this
 * the moment the record opens, and writing to form state on mount would mark
 * the form dirty before the user has touched anything - which enables Save on
 * an untouched record and trips the unsaved-changes guard on the way out.
 *
 * This follows the pattern the processing screen already uses for the donor
 * signature: a module-level value that the toolbar folds into the payload at
 * submit time.
 *
 * What the user actually edits - lotnumber_* and the quantity fields - still
 * goes through the form, so picking a lot dirties the record exactly as typing
 * one used to.
 */

const EMPTY = { refId: null, loaded: false, lines: [], committed: {} };

let state = EMPTY;
const listeners = new Set();

const emit = () => { listeners.forEach(fn => fn()); };

const subscribe = (listener) => {
    listeners.add(listener);
    return () => { listeners.delete(listener); };
};

const getState = () => state;

/**
 * Point the store at a donation, always from a clean slate.
 *
 * Unconditional on purpose: reopening the same record reloads its form fields
 * from the server, so any unsaved picks left in here would no longer match the
 * lot numbers on screen. And clearing on every open is what stops a selection
 * leaking from one donor to the next.
 */
export const beginRecord = (refId) => {
    state = { refId: refId || null, loaded: false, lines: [], committed: {} };
    emit();
};

/** Seed from the ledger: what this donation already drew down, per lot. */
export const setLoaded = (refId, lines, committed) => {
    state = {
        refId: refId || null,
        loaded: true,
        lines: lines || [],
        committed: committed || {},
    };
    emit();
};

/** Mark the load as failed - the save then leaves inventory alone. */
export const setLoadFailed = (refId) => {
    state = { refId: refId || null, loaded: false, lines: [], committed: {} };
    emit();
};

/** Replace one field's slice of the selection, leaving the others alone. */
export const setFieldLines = (field, lines) => {
    state = {
        ...state,
        lines: state.lines
            .filter(line => line && line.phlebo_field !== field)
            .concat(lines || []),
    };
    emit();
};

/** Read-only snapshot, for the save transform. */
export const snapshot = () => state;

/** Subscribe a component to the store. */
export const useSoftGoods = () => useSyncExternalStore(subscribe, getState, getState);

/** Just one field's lines. */
export const linesForField = (currentState, field) =>
    (currentState.lines || []).filter(line => line && line.phlebo_field === field);

export default useSoftGoods;
